import { useGetList } from "ra-core";
import { Link } from "react-router";

import { MESSAGE_TOKENS } from "./tokenModel";

import { cn } from "@/lib/utils";

/**
 * Insert-at-cursor token chips.
 *
 * Typing `{{first_name}}` by hand is easy to get subtly wrong (a typo is
 * stripped silently on send), so the chips write the exact supported string.
 */
export const TokenChips = ({
  onInsert,
  className,
}: {
  onInsert: (token: string) => void;
  className?: string;
}) => (
  <div className={cn("flex flex-wrap items-center gap-1", className)}>
    <span className="text-[11.5px] text-muted-foreground">Insert:</span>
    {MESSAGE_TOKENS.map((item) => (
      <button
        key={item.token}
        type="button"
        onClick={() => onInsert(item.token)}
        className="rounded-full border border-border bg-card px-2 py-0.5 text-[11.5px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        {item.label}
      </button>
    ))}
  </div>
);

interface Template {
  id: string;
  name: string;
  channel: string;
  subject: string | null;
  body: string;
}

/**
 * Pick a saved message template into a composer.
 *
 * Filtered to the channel in play, because offering an email template with a
 * subject line for an SMS send produces a message that silently drops half of
 * what was chosen.
 */
export const TemplatePicker = ({
  channel,
  onPick,
  className,
}: {
  channel: string;
  onPick: (template: Template) => void;
  className?: string;
}) => {
  const { data: templates } = useGetList<Template>("message_templates", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
    filter: { channel },
  });

  const list = templates ?? [];

  // A workspace that has never installed a template pack has none of these.
  // Rendering nothing at all made the feature look absent rather than empty,
  // so point at where templates come from instead of silently disappearing.
  if (list.length === 0) {
    return (
      <p className={cn("text-[11.5px] text-muted-foreground", className)}>
        No saved {channel === "sms" ? "text" : "email"} templates yet,{" "}
        <Link to="/settings" className="underline">
          add some in Settings
        </Link>
        .
      </p>
    );
  }

  return (
    <label
      className={cn(
        "flex flex-col gap-1 text-[11.5px] text-muted-foreground",
        className,
      )}
    >
      Start from a template
      <select
        className="h-8 rounded-md border border-input bg-transparent px-2 text-[12.5px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
        value=""
        onChange={(event) => {
          const found = list.find((t) => t.id === event.target.value);
          if (found) onPick(found);
        }}
      >
        <option value="">Choose…</option>
        {list.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </select>
    </label>
  );
};
